import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TextAreaFieldComponent } from '../../../../components/form/textarea-field/textarea-field.component';
import { ComplaintsService } from '../../../../services/api/complaints.service';
import { AuthService } from '../../../../services/api/auth.service';
import { AlertNotificationService } from '../../../../services/alert-notification.service';
import { MainLayoutService } from '../../../../services/main-layout.service';
import { CommonService } from '../../../../services/common.service';
import { NgbAlert } from '@ng-bootstrap/ng-bootstrap';
import { AlertInfo } from '../../../../models/alert-info';
import { HttpParams } from '@angular/common/http';
import { ConfirmationDialogService } from '../../../../services/confirmation-dialog.service';

interface SpeechMessage {
  complaintMessageId?: number;
  complaintId?: number;
  userId?: number;
  text: string;
  user?: {
    firstName: string;
    lastName: string;
    userId?: number;
  };
  createTime: string | Date;
}

@Component({
  selector: 'complaints-speech',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TextAreaFieldComponent,
    NgbAlert
  ],
  templateUrl: './complaints-speech.component.html',
  styleUrl: './complaints-speech.component.scss'
})
export class ComplaintsSpeechComponent implements OnInit, OnChanges {
  @Input() complaintId?: number;
  @Input() complaint: any;
  @Input() readonly: boolean = false;
  @Input() isTakingCare: boolean = false;
  @Output() onStatusChange = new EventEmitter<string>();

  speechForm: FormGroup;
  messages: SpeechMessage[] = [];
  alertInfo: AlertInfo = { show: false, type: '', message: '' };
  currentUser: any;
  currentDate: Date = new Date();
  isCustomerProfile: boolean = false;
  messageToDelete: number | null = null;

  constructor(
    private fb: FormBuilder,
    private complaintsService: ComplaintsService,
    private authService: AuthService,
    private alertNotificationService: AlertNotificationService,
    private mainLayoutService: MainLayoutService,
    private commonService: CommonService,
    private confirmationDialogService: ConfirmationDialogService
  ) {
    this.speechForm = this.fb.group({
      message: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    this.loadCurrentUser();
    this.checkUserProfile();
    if (this.complaintId) {
      this.loadMessages();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['complaintId'] && changes['complaintId'].currentValue) {
      this.loadMessages();
    }
  }

  private loadCurrentUser(): void {
    this.authService.getUserInSession().subscribe(user => {
      if (user) {
        this.currentUser = user;
      }
    });
  }

  private checkUserProfile(): void {
    this.mainLayoutService.activeProfile$.subscribe(profileCode => {
      const customerProfiles = ['CUS1', 'CUS2', 'CUS3'];
      this.isCustomerProfile = profileCode ? customerProfiles.includes(profileCode) : false;
    });
  }

  private loadMessages(): void {
    if (!this.complaintId) return;

    const params = new HttpParams().set('size', '9999');
    this.complaintsService.speechComplaintList(this.complaintId, { params }).subscribe({
      next: (response: any) => {
        if (response.status?.code === 1073741824 || response.status?.code === 200) {
          const complaintMessages = response.data?.complaintMessages || [];
          this.messages = complaintMessages.map((msg: any) => ({
            complaintMessageId: msg.complaintMessageId,
            complaintId: msg.complaintId,
            userId: msg.userId,
            text: msg.text,
            createTime: msg.createTime,
            user: msg.user || {
              firstName: msg.firstName || 'Unknown',
              lastName: msg.lastName || 'User',
              userId: msg.userId
            }
          }));
        }
      },
      error: (error: any) => {
        console.error('Error loading messages:', error);
        this.alertNotificationService.error('Failed to load messages');
      }
    });
  }

  getMessageControl(): FormControl {
    return this.speechForm.get('message') as FormControl;
  }

  sendMessage(): void {
    if (this.speechForm.invalid || !this.complaintId) return;

    const messageContent = this.getMessageControl().value;

    if (messageContent && messageContent.trim() && this.currentUser) {
      const messageData = {
        text: messageContent,
        userId: this.currentUser.userId,
        complaintId: this.complaintId
      };

      this.complaintsService.speechComplaintCreate(this.complaintId, messageData).subscribe({
        next: (response: any) => {
          if (response.status?.code === 1073741824 || response.status?.code === 200 || response.status?.code === 201) {
            this.speechForm.reset();
            this.alertNotificationService.success('Message sent successfully');
            this.loadMessages();
            
            const newStatus = this.isCustomerProfile ? 'WAITING_FOR_OPERATOR' : 'WAITING_FOR_CUSTOMER';
            this.onStatusChange.emit(newStatus);
          } else {
            this.alertNotificationService.error('Failed to send message');
          }
        },
        error: (error: any) => {
          this.alertNotificationService.error('Failed to send message');
          console.error('Error sending message:', error);
        }
      });
    }
  }

  confirmDelete(complaintMessageId: number): void {
    const message = 'Are you sure you want to delete this message?';
    this.messageToDelete = complaintMessageId;
    
    this.confirmationDialogService.open(
      message,
      () => this.deleteMessage(),
      () => { this.messageToDelete = null; }
    );
  }

  deleteMessage(): void {
    if (!this.complaintId || !this.messageToDelete) return;

    this.complaintsService.speechComplaintDelete(this.complaintId, this.messageToDelete).subscribe({
      next: (response: any) => {
        if (response.status?.code === 1073741824 || response.status?.code === 200 || response.status?.code === 204) {
          this.alertNotificationService.success('Message deleted successfully');
          this.loadMessages();
          this.messageToDelete = null;
        } else {
          this.alertNotificationService.error('Failed to delete message');
        }
      },
      error: (error: any) => {
        this.alertNotificationService.error('Failed to delete message');
        console.error('Error deleting message:', error);
      }
    });
  }

  isCurrentUserMessage(message: any): boolean {
    if (!this.currentUser || !message.user) {
      return false;
    }
    
    if (message.user.userId && this.currentUser.userId) {
      return Number(message.user.userId) === Number(this.currentUser.userId);
    }
    
    if (this.currentUser.firstName && this.currentUser.lastName &&
        message.user.firstName && message.user.lastName) {
      return message.user.firstName === this.currentUser.firstName &&
             message.user.lastName === this.currentUser.lastName;
    }
    
    return false;
  }

  canSendMessage(): boolean {
    return this.isTakingCare || (this.isCustomerProfile && this.messages.length > 0);
  }

  formatMessageDate(date: string | Date): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return this.commonService.formatDate(dateObj);
  }

  showAlertInfo(type: string, message: string): void {
    this.alertInfo = { show: true, type, message };
    setTimeout(() => {
      this.alertInfo.show = false;
    }, 3000);
  }
}
